import { forwardRef } from "react";
import { cx } from "./cx";
import { Spinner } from "./Spinner";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "success" | "outline" | "link";
type Size = "xs" | "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** When true: shows spinner + disables. Keeps width via opacity, no jump. */
  loading?: boolean;
  /** Icon shown left of label (visual only — must be aria-hidden by caller if decorative). */
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** Renders as `display: block; width: 100%;` */
  block?: boolean;
  /** When true the button is purely an icon — pass an aria-label! */
  iconOnly?: boolean;
}

const base =
  "inline-flex items-center justify-center gap-1.5 font-medium select-none " +
  "transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 " +
  "rounded-md border";

const sizes: Record<Size, string> = {
  xs: "h-8 px-2.5 text-xs",
  sm: "h-9 px-3 text-sm",
  md: "h-11 px-4 text-sm min-h-tap",   // hits WCAG touch target by default
  lg: "h-12 px-5 text-base min-h-tap",
};

const variants: Record<Variant, string> = {
  primary:
    "bg-[var(--c-brand-strong)] text-white border-[var(--c-brand-strong)] " +
    "hover:bg-[var(--c-brand)] hover:border-[var(--c-brand)]",
  secondary:
    "bg-[var(--c-brand-soft)] text-[var(--c-brand-strong)] border-[var(--c-brand-soft)] " +
    "hover:bg-[var(--c-brand)] hover:text-white hover:border-[var(--c-brand)]",
  outline:
    "bg-transparent text-[var(--c-text-default)] border-[var(--c-border-default)] " +
    "hover:bg-[var(--c-surface-muted)]",
  ghost:
    "bg-transparent text-[var(--c-text-default)] border-transparent " +
    "hover:bg-[var(--c-surface-muted)]",
  danger:
    "bg-[var(--c-danger)] text-white border-[var(--c-danger)] hover:brightness-110",
  success:
    "bg-[var(--c-success)] text-white border-[var(--c-success)] hover:brightness-110",
  link:
    "bg-transparent text-[var(--c-brand)] border-transparent underline-offset-2 " +
    "hover:underline px-0 h-auto",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading,
    leftIcon,
    rightIcon,
    block,
    iconOnly,
    type = "button",
    className,
    children,
    disabled,
    ...rest
  },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        base,
        sizes[size],
        variants[variant],
        block && "w-full",
        iconOnly && "px-0 aspect-square",
        className
      )}
      {...rest}
    >
      {loading ? (
        <Spinner size={size === "xs" || size === "sm" ? "xs" : "sm"} aria-hidden />
      ) : (
        leftIcon && <span aria-hidden className="inline-flex">{leftIcon}</span>
      )}
      {!iconOnly && <span>{children}</span>}
      {rightIcon && !loading && <span aria-hidden className="inline-flex">{rightIcon}</span>}
    </button>
  );
});
